/**
 * Capture the visible app viewport as an image File for feedback attachments.
 * Uses html2canvas (already in the TraceKit workspace) — no browser extension.
 */

export interface CapturePageScreenshotOptions {
  /** Output filename (default: feedback-screenshot-<timestamp>.jpg) */
  filename?: string;
  /** JPEG quality 0–1 (default: 0.85) */
  quality?: number;
  /** Cap the longest side in CSS pixels before encode (default: 1920) */
  maxDimension?: number;
  /**
   * Optional CSS selector(s) for nodes to hide in the clone during capture
   * (e.g. the feedback dialog itself as a safety net).
   */
  hideSelectors?: string[];
  /** Delay before capture so dropdowns/overlays settle (ms). Default: 80 */
  settleMs?: number;
}

const DEFAULT_QUALITY = 0.85;
const DEFAULT_MAX_DIMENSION = 1920;
const DEFAULT_SETTLE_MS = 80;

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function maskSensitiveInputs(doc: Document): void {
  doc.querySelectorAll('input[type="password"]').forEach((node) => {
    const input = node as HTMLInputElement;
    if (!input.value) return;
    input.value = '••••••••';
    input.setAttribute('value', '••••••••');
  });
}

function hideNodes(doc: Document, selectors: string[]): void {
  for (const selector of selectors) {
    doc.querySelectorAll(selector).forEach((el) => {
      (el as HTMLElement).style.setProperty('visibility', 'hidden', 'important');
    });
  }
}

function scaleCanvasIfNeeded(source: HTMLCanvasElement, maxDimension: number): HTMLCanvasElement {
  const longest = Math.max(source.width, source.height);
  if (longest <= maxDimension || maxDimension <= 0) {
    return source;
  }
  const scale = maxDimension / longest;
  const width = Math.max(1, Math.round(source.width * scale));
  const height = Math.max(1, Math.round(source.height * scale));
  const scaled = document.createElement('canvas');
  scaled.width = width;
  scaled.height = height;
  const ctx = scaled.getContext('2d');
  if (!ctx) {
    return source;
  }
  ctx.drawImage(source, 0, 0, width, height);
  return scaled;
}

function canvasToJpegBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to encode screenshot as JPEG'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      quality
    );
  });
}

/**
 * Screenshot the current viewport and return a JPEG File suitable for FormData upload.
 */
export async function capturePageScreenshot(
  options: CapturePageScreenshotOptions = {}
): Promise<File> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    throw new Error('Page screenshot is only available in the browser');
  }

  const {
    quality = DEFAULT_QUALITY,
    maxDimension = DEFAULT_MAX_DIMENSION,
    hideSelectors = [],
    settleMs = DEFAULT_SETTLE_MS,
  } = options;

  const filename =
    options.filename ??
    `feedback-screenshot-${new Date().toISOString().replace(/[:.]/g, '-')}.jpg`;

  if (settleMs > 0) {
    await wait(settleMs);
  }

  const html2canvas = (await import('html2canvas')).default;

  const width = window.innerWidth;
  const height = window.innerHeight;

  const canvas = await html2canvas(document.documentElement, {
    useCORS: true,
    allowTaint: true,
    logging: false,
    backgroundColor: '#ffffff',
    scale: Math.min(window.devicePixelRatio || 1, 2),
    x: window.scrollX,
    y: window.scrollY,
    width,
    height,
    windowWidth: document.documentElement.clientWidth,
    windowHeight: document.documentElement.clientHeight,
    scrollX: -window.scrollX,
    scrollY: -window.scrollY,
    onclone: (clonedDoc) => {
      maskSensitiveInputs(clonedDoc);
      if (hideSelectors.length > 0) {
        hideNodes(clonedDoc, hideSelectors);
      }
    },
  });

  const output = scaleCanvasIfNeeded(canvas, maxDimension);
  const blob = await canvasToJpegBlob(output, quality);
  return new File([blob], filename, { type: 'image/jpeg', lastModified: Date.now() });
}
